import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from '../utils/axiosInstance';
import { BASE_URL, ROOT_DOMAIN } from '@/config/constants';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import artistIntroLogoWhite from "../assets/artist_intro_white.svg";
import PixelBlast from '../components/PixelBlast';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { LEGAL_URLS } from '@/lib/consent';
import { useConsent } from '@/context/ConsentContext';

interface User {
  _id: string;
  username: string;
  user_type: string;
  email: string;
  domain: string;
}

type SignupStep = 'details' | 'otp' | 'password';

const Signup: React.FC<{ onLogin: (token: string, user: User) => void; onSwitchToLogin: () => void }> = ({ onLogin, onSwitchToLogin }) => {
  const consent = useConsent();
  const [signupStep, setSignupStep] = useState<SignupStep>('details');
  const [signupArtistName, setSignupArtistName] = useState('');
  const [signupSlug, setSignupSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');
  const [slugMessage, setSlugMessage] = useState('');
  const [slugSuggestions, setSlugSuggestions] = useState<string[]>([]);
  const slugCheckSeq = useRef(0);
  const [signupEmail, setSignupEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const emailCheckSeq = useRef(0);
  const [signupConsent, setSignupConsent] = useState(false);
  const [signupLegalConsent, setSignupLegalConsent] = useState(false);
  const [signupOtp, setSignupOtp] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
  const [signupError, setSignupError] = useState('');
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [slugConvertedHint, setSlugConvertedHint] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const otpInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  // Prevent document.body scroll — all scrolling must happen inside the inner container
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Delayed focus on step change — avoids iOS Safari body-scroll on autoFocus
  useEffect(() => {
    if (signupStep === 'otp') {
      const t = window.setTimeout(() => otpInputRef.current?.focus(), 100);
      return () => window.clearTimeout(t);
    }
    if (signupStep === 'password') {
      const t = window.setTimeout(() => passwordInputRef.current?.focus(), 100);
      return () => window.clearTimeout(t);
    }
  }, [signupStep]);

  // UX cap mirrors backend SLUG_MAX_LENGTH (30). Schema still allows 80.
  const SLUG_MAX_LEN = 30;

  // Derive a safe slug candidate from the artist name (used to seed the slug field).
  // Only the FIRST name (first whitespace-delimited word) is used, so a full
  // "First Last" name yields a slug of just "first".
  const derivedSlugFromName = useMemo(() => {
    const firstName = signupArtistName.trim().split(/\s+/)[0] || '';
    return firstName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, SLUG_MAX_LEN);
  }, [signupArtistName]);

  // Sanitize input as the user types — strip everything except a-z0-9 and hyphen.
  // Whitespace becomes a hyphen; uppercase becomes lowercase. Leading hyphens stripped.
  const sanitizeSlugInput = (raw: string): string => {
    return raw
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-+/, '')
      .slice(0, SLUG_MAX_LEN);
  };

  // Auto-fill slug from artist name until the user manually edits it.
  useEffect(() => {
    if (!slugTouched) {
      setSignupSlug(derivedSlugFromName);
    }
  }, [derivedSlugFromName, slugTouched]);

  // Auto-clear the "Converted to:" hint shortly after it appears.
  useEffect(() => {
    if (!slugConvertedHint) return;
    const t = window.setTimeout(() => setSlugConvertedHint(''), 2500);
    return () => window.clearTimeout(t);
  }, [slugConvertedHint]);

  // Debounced slug availability check.
  useEffect(() => {
    const slug = signupSlug.trim();
    if (!slug) {
      setSlugStatus('idle');
      setSlugMessage('');
      setSlugSuggestions([]);
      return;
    }

    // Local format check first — must mirror backend SLUG_REGEX.
    if (slug.length < 2) {
      setSlugStatus('invalid');
      setSlugMessage('Must be at least 2 characters');
      setSlugSuggestions([]);
      return;
    }
    if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug)) {
      setSlugStatus('invalid');
      setSlugMessage('Use only lowercase letters, numbers, and hyphens');
      setSlugSuggestions([]);
      return;
    }
    if (slug.length > SLUG_MAX_LEN) {
      setSlugStatus('invalid');
      setSlugMessage(`Must be ${SLUG_MAX_LEN} characters or fewer`);
      setSlugSuggestions([]);
      return;
    }

    setSlugStatus('checking');
    setSlugMessage('');
    setSlugSuggestions([]);
    const seq = ++slugCheckSeq.current;
    const timer = window.setTimeout(async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/domain/check-slug/${encodeURIComponent(slug)}`);
        if (seq !== slugCheckSeq.current) return; // stale response
        if (res.data?.available) {
          setSlugStatus('available');
          setSlugMessage('');
          setSlugSuggestions([]);
        } else {
          setSlugStatus('taken');
          setSlugMessage(res.data?.reason || 'This name is not available');
          setSlugSuggestions(Array.isArray(res.data?.suggestions) ? res.data.suggestions : []);
        }
      } catch {
        if (seq !== slugCheckSeq.current) return;
        setSlugStatus('idle');
        setSlugMessage('');
        setSlugSuggestions([]);
      }
    }, 400);

    return () => window.clearTimeout(timer);
  }, [signupSlug]);

  useEffect(() => {
    const email = signupEmail.trim().toLowerCase();
    const seq = ++emailCheckSeq.current;

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailStatus('idle');
      return;
    }

    setEmailStatus('checking');
    const timer = window.setTimeout(async () => {
      try {
        const res = await axios.post(`${BASE_URL}/api/auth/check-email`, { email });
        if (seq !== emailCheckSeq.current) return;
        setEmailStatus(res.data?.available ? 'available' : 'taken');
      } catch {
        if (seq !== emailCheckSeq.current) return;
        setEmailStatus('idle');
      }
    }, 500);

    return () => window.clearTimeout(timer);
  }, [signupEmail]);

  // Countdown timer for OTP resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = window.setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [resendCooldown]);

  // Step 1: Send OTP
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignupLoading(true);
    setSignupError('');

    try {
      await axios.post(BASE_URL + '/api/auth/send-otp', {
        email: signupEmail,
        artistName: signupArtistName,
      });
      toast.success('Verification code sent to your email');
      setResendCooldown(30);
      setSignupStep('otp');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to send verification code';
      setSignupError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSignupLoading(false);
    }
  };

  // Resend OTP (from OTP step, no navigation)
  const handleResendOtp = async () => {
    setResendLoading(true);
    setSignupError('');
    setSignupOtp('');
    try {
      await axios.post(BASE_URL + '/api/auth/send-otp', {
        email: signupEmail,
        artistName: signupArtistName,
      });
      toast.success('New verification code sent');
      setResendCooldown(30);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to resend code';
      setSignupError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignupLoading(true);
    setSignupError('');

    try {
      await axios.post(BASE_URL + '/api/auth/verify-otp', {
        email: signupEmail,
        code: signupOtp,
      });
      toast.success('Email verified successfully');
      setSignupStep('password');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Invalid verification code';
      setSignupError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSignupLoading(false);
    }
  };

  // Step 3: Complete registration
  const handleCompleteSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignupError('');

    if (signupPassword !== signupConfirmPassword) {
      setSignupError('Passwords do not match');
      return;
    }
    if (signupPassword.length < 6) {
      setSignupError('Password must be at least 6 characters');
      return;
    }

    setSignupLoading(true);

    try {
      const startTime = Date.now();
      const res = await axios.post(BASE_URL + '/api/auth/register', {
        artistName: signupArtistName,
        slug: signupSlug.trim(),
        email: signupEmail,
        password: signupPassword,
      });
      const { token, user } = res.data;

      toast.success('Your website is ready. Let\'s set it up.');
      setSignupSuccess(true);

      const elapsed = Date.now() - startTime;
      if (elapsed < 300) {
        await new Promise(resolve => setTimeout(resolve, 300 - elapsed));
      }

      // onLogin writes the token to storage — axiosInstance's request
      // interceptor pulls from there. Call it FIRST so the consent POST
      // below carries a valid Authorization header.
      onLogin(token, user);

      // Flip ConsentContext to authenticated so the cookie-notice
      // acknowledgment POSTs to /api/consent (attributed to the new
      // userId). The App component's token effect will re-set this to the
      // same value on next render — idempotent.
      consent.setAuthenticated(true);
      consent.acknowledge('signup');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Registration failed';
      setSignupError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSignupLoading(false);
    }
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2 bg-white">
      {/* Branded Side */}
      <div className="hidden lg:flex flex-col justify-between bg-primary pt-20 pb-20 p-16 text-white overflow-hidden relative">
        <PixelBlast
          className="absolute! inset-0"
          color="#3b82f6"
          variant="circle"
          pixelSize={4}
          speed={0.3}
          patternDensity={1.8}
          edgeFade={0}
          enableRipples
          rippleSpeed={0.25}
          rippleThickness={0.12}
        />
        <div className="relative z-10">
          <img src={artistIntroLogoWhite} alt="Artist Intro" className="h-14" />
        </div>
        <div className="relative z-10">
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">Get Started</h1>
          <p className="text-base sm:text-lg  text-white/80 max-w-md">
            {signupStep === 'otp'
              ? 'We sent a verification code to your email. Enter it to continue.'
              : signupStep === 'password'
                ? 'Almost there! Set a password to secure your account.'
                : 'Create your artist website in minutes. Add music, events, and media — then publish instantly.'}
          </p>
        </div>
      </div>

      {/* Form Side */}
      <div className="relative flex flex-col h-dvh bg-white">
        {/* Mobile branding header */}
        <div className="lg:hidden bg-primary px-6 py-5 shrink-0">
          <img src={artistIntroLogoWhite} alt="Artist Intro" className="h-7" />
        </div>

        <div className="flex-1 min-h-0 flex flex-col p-6 sm:p-12 lg:px-20 lg:pt-20 lg:pb-12">
          <div className="w-full max-w-md mx-auto flex flex-col flex-1 min-h-0">
            {/* ── Fixed top: heading + stepper + role ── */}
            <div className="shrink-0 space-y-6">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground text-center">
                {signupStep === 'details' && <>Create your <span className="text-primary">Artist Intro</span></>}
                {signupStep === 'otp' && 'Verify your email'}
                {signupStep === 'password' && 'Set your password'}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground text-center">
                {signupStep === 'details' && 'Get your digital identity live in minutes'}
                {signupStep === 'otp' && <>Enter the 6-digit code sent to <span className="text-primary font-medium">{signupEmail}</span></>}
                {signupStep === 'password' && 'Choose a secure password for your account'}
              </p>
            </div>

            {/* Step indicator */}
            {(() => {
              const steps: SignupStep[] = ['details', 'otp', 'password'];
              const currentIdx = steps.indexOf(signupStep);
              return (
                <div className="flex items-center">
  {steps.map((step, i) => (
    <React.Fragment key={step}>
      {/* Dot */}
      <div className={`shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
        i < currentIdx
          ? 'bg-primary text-white' // Completed step: primary background, white text
          : i === currentIdx
            ? 'border-2 border-primary bg-primary text-white' // Current step: primary background, white text
            : 'border-2 border-border bg-white text-gray-400' // Future step: white background, gray text
      }`}>
        {i + 1}
      </div>
      {/* Connector line */}
      {i < steps.length - 1 && (
        <div className={`flex-1 h-0.5 transition-colors duration-300 ${
          i < currentIdx ? 'bg-primary' : 'bg-border'
        }`} />
      )}
    </React.Fragment>
  ))}
</div>
              );
            })()}

          </div>

          {/* ── Scrollable middle ── */}
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden mt-6 scrollbar-hide px-1 -mx-1" style={{ scrollbarWidth: 'none' }}>

          {/* ─── STEP 1: Details ─── */}
          {signupStep === 'details' && (
            <form onSubmit={handleSendOtp} className="space-y-6">
              <div className="space-y-5">
                {/* Artist Name */}
                <div>
                  <label htmlFor="signup-artist-name" className="block text-sm font-semibold text-foreground mb-2">
                    Artist / Band Name
                  </label>
                  <Input
                    id="signup-artist-name"
                    type="text"
                    required
                    value={signupArtistName}
                    onChange={e => { setSignupArtistName(e.target.value.replace(/^\s+/, '')); setSignupError(''); }}
                    className=""
                    placeholder="Artist Name"
                  />
                </div>

                {/* Slug / Website name */}
                <div>
                  <label htmlFor="signup-slug" className="block text-sm font-semibold text-foreground mb-2">
                    Choose your website link
                  </label>
                  <Input
                    id="signup-slug"
                    type="text"
                    required
                    autoComplete="off"
                    spellCheck={false}
                    maxLength={SLUG_MAX_LEN}
                    value={signupSlug}
                    onChange={e => {
                      setSlugTouched(true);
                      const raw = e.target.value;
                      const cleaned = sanitizeSlugInput(raw);
                      // If sanitization actually changed the input, surface a hint
                      // so the user understands why their keystrokes were rewritten.
                      if (raw && raw !== cleaned && cleaned) {
                        setSlugConvertedHint(`Converted to: ${cleaned}`);
                      } else {
                        setSlugConvertedHint('');
                      }
                      setSignupSlug(cleaned);
                      setSignupError('');
                    }}
                    placeholder="your-name"
                    suffix={`.${ROOT_DOMAIN}`}
                  />
                  {/* Status line */}
                  <div className="mt-1.5 min-h-5 text-xs">
                    {slugConvertedHint && slugStatus !== 'taken' && slugStatus !== 'invalid' && (
                      <span className="text-muted-foreground">{slugConvertedHint}</span>
                    )}
                    {!slugConvertedHint && slugStatus === 'checking' && (
                      <span className="text-muted-foreground">Checking availability…</span>
                    )}
                    {!slugConvertedHint && slugStatus === 'available' && signupSlug && (
                      <span className="text-primary font-medium text-sm">
                       ✓ {signupSlug}.{ROOT_DOMAIN} is available
                      </span>
                    )}
                    {slugStatus === 'invalid' && (
                      <span className="text-danger">{slugMessage}</span>
                    )}
                    {slugStatus === 'taken' && (
                      <div className="space-y-1">
                        <span className="text-danger">{slugMessage}</span>
                        {slugSuggestions.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                            <span className="text-muted-foreground">Try:</span>
                            {slugSuggestions.map((s) => (
                              <button
                                key={s}
                                type="button"
                                onClick={() => { setSlugTouched(true); setSignupSlug(s); }}
                                className="px-2 py-0.5 rounded border border-input bg-muted/40 hover:bg-muted text-foreground transition-colors"
                              >
                                {s}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {slugStatus === 'idle' && !signupSlug && (
                      <span className="text-muted-foreground">Lowercase letters, numbers, and hyphens only</span>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="signup-email" className="block text-sm font-semibold text-foreground mb-2">
                    Email <span className="text-muted-foreground">(This will also be your username)</span>
                  </label>
                  <Input
                    id="signup-email"
                    type="email"
                    required
                    value={signupEmail}
                    onChange={e => { setSignupEmail(e.target.value.trim()); setSignupError(''); }}
                    className=""
                    placeholder="name@example.com"
                  />
                  <div className="mt-1.5 min-h-5 text-xs">
                    {emailStatus === 'checking' && (
                      <span className="text-muted-foreground">Checking email...</span>
                    )}
                    {emailStatus === 'taken' && (
                      <span className="text-danger">An account with this email already exists</span>
                    )}
                  </div>
                </div>

                {/* Identity confirmation */}
                <div className="flex items-start gap-2.5">
                  <Checkbox
                    id="signup-consent"
                    checked={signupConsent}
                    onCheckedChange={(checked) => setSignupConsent(checked === true)}
                    className="mt-0.5"
                  />
                  <label htmlFor="signup-consent" className="text-sm font-medium text-foreground cursor-pointer leading-snug">
                    I confirm, I am the artist mentioned above.
                  </label>
                </div>

                {/* Terms + Privacy legal consent — mandatory */}
                <div className="flex items-start gap-2.5">
                  <Checkbox
                    id="signup-legal-consent"
                    checked={signupLegalConsent}
                    onCheckedChange={(checked) => setSignupLegalConsent(checked === true)}
                    className="mt-0.5"
                  />
                  <label htmlFor="signup-legal-consent" className="text-sm font-medium text-foreground cursor-pointer leading-snug">
                    I agree to the{' '}
                    <a href={LEGAL_URLS.terms} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href={LEGAL_URLS.privacy} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80">
                      Privacy Policy
                    </a>
                    .
                  </label>
                </div>
              </div>

              {signupError && (
                <div className="text-sm text-danger bg-danger-light border border-danger/20 rounded-md p-4">
                  {signupError}
                </div>
              )}

              <Button
                type="submit"
                size="lg-full"
                disabled={
                  signupLoading
                  || !signupConsent
                  || !signupLegalConsent
                  || slugStatus !== 'available'
                  || emailStatus === 'checking'
                  || emailStatus === 'taken'
                }
                loading={signupLoading}
                loadingText="Sending OTP..."
              >
                Send OTP
              </Button>

              {/* OR divider */}
              {/* <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-3 text-muted-foreground">OR</span>
                </div>
              </div> */}

              {/* Google Sign Up (placeholder) */}
              {/* <Button
                type="button"
                variant="outline"
                disabled
                size="lg-full"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Sign up with Google
              </Button> */}
            </form>
          )}

          {/* ─── STEP 2: OTP Verification ─── */}
          {signupStep === 'otp' && (
            <form onSubmit={handleVerifyOtp} className="space-y-6">
              <div className="space-y-5">
                <div>
                  <label htmlFor="signup-otp" className="block text-sm font-semibold text-foreground mb-2">
                    Verification Code
                  </label>
                  <Input
                    id="signup-otp"
                    type="text"
                    required
                    maxLength={6}
                    value={signupOtp}
                    onChange={e => { setSignupOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setSignupError(''); }}
                    className="text-lg text-center tracking-[0.3em] font-mono"
                    placeholder="000000"
                    ref={otpInputRef}
                  />
                  <p className="mt-2 text-xs text-muted-foreground">
                    Didn't receive the code?{' '}
                    {resendCooldown > 0 ? (
                      <span className="text-muted-foreground">Resend in {resendCooldown}s</span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={resendLoading}
                        className="text-primary font-medium hover:text-primary/80 disabled:opacity-50"
                      >
                        {resendLoading ? 'Sending…' : 'Resend'}
                      </button>
                    )}
                  </p>
                </div>
              </div>

              {signupError && (
                <div className="text-sm text-danger bg-danger-light border border-danger/20 rounded-md p-4">
                  {signupError}
                </div>
              )}

              <Button
                type="submit"
                size="lg-full"
                disabled={signupLoading || signupOtp.length !== 6}
                loading={signupLoading}
                loadingText="Verifying..."
              >
                Verify Email
              </Button>
            </form>
          )}

          {/* ─── STEP 3: Password Creation ─── */}
          {signupStep === 'password' && (
            <form onSubmit={handleCompleteSignup} className="space-y-6">
              <div className="space-y-5">
                <div>
                  <label htmlFor="signup-password" className="block text-sm font-semibold text-foreground mb-2">
                    Password
                  </label>
                  <PasswordInput
                    id="signup-password"
                    required
                    minLength={8}
                    value={signupPassword}
                    onChange={e => { setSignupPassword(e.target.value); setSignupError(''); }}
                    placeholder="Minimum 8 characters"
                    ref={passwordInputRef}
                    showStrengthMeter 
    showCopyBtn
    showRequirements
                  />
                </div>
                <div>
                  <label htmlFor="signup-confirm-password" className="block text-sm font-semibold text-foreground mb-2">
                    Confirm Password
                  </label>
                  <PasswordInput
                    id="signup-confirm-password"
                    required
                    minLength={6}
                    value={signupConfirmPassword}
                    onChange={e => { setSignupConfirmPassword(e.target.value); setSignupError(''); }}
                    placeholder="Re-enter your password"
                  />
                </div>
              </div>

              {signupError && (
                <div className="text-sm text-danger bg-danger-light border border-danger/20 rounded-md p-4">
                  {signupError}
                </div>
              )}

              {signupSuccess && (
                <div className="text-sm text-primary bg-primary/10 border border-primary/20 rounded-md p-4 text-center font-medium">
                  Your website is ready. Let's set it up.
                </div>
              )}

              <Button
                type="submit"
                size="lg-full"
                disabled={signupLoading || signupSuccess}
                loading={signupLoading}
                loadingText="Creating account..."
              >
                Create Account
              </Button>
            </form>
          )}

          <p className="text-center text-sm text-muted-foreground py-4">
            Already have an account?{' '}
            <button type="button" onClick={onSwitchToLogin} className="font-semibold text-primary hover:text-primary/80 transition-colors cursor-pointer">
              Log in
            </button>
          </p>

          </div>{/* end scrollable middle */}

          {/* ── Fixed bottom: footer links ── */}
          <div className="shrink-0 pt-4 flex justify-center gap-3 sm:gap-6 text-[10px] sm:text-xs tracking-wider text-muted-foreground">
            <a href={LEGAL_URLS.terms} target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors uppercase">Terms of Service</a>
            <a href={LEGAL_URLS.privacy} target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors uppercase">Privacy Policy</a>
            <a href={LEGAL_URLS.cookies} target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors uppercase">Cookie Policy</a>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
